'use client';

import { Component, PropsWithChildren, ReactNode } from 'react';

import { getHealthCheckApi } from '@flipster/apis/v2/health';
import { checkMaintenanceError } from '@flipster/utils/axios';
import { isServer } from '@flipster/utils/env-helper';

import Error from '../../app/error';

interface Props extends PropsWithChildren {
  fallback?: (props: { error: Error; reset: () => void }) => ReactNode;
}
interface State {
  error: undefined | Error;
}

class ErrorBoundary extends Component<Props, State> {
  interval: NodeJS.Timeout | null;

  constructor(props: Props) {
    super(props);
    this.interval = null;
    // Define a state variable to track whether is an error or not
    this.state = { error: undefined };
  }

  static getDerivedStateFromError(error: unknown) {
    // Update state so the next render will show the fallback UI
    return { error };
  }

  componentDidCatch(error: unknown) {
    if (checkMaintenanceError(error)) {
      this.interval = setInterval(() => {
        if (isServer) return;
        getHealthCheckApi().then(() => window.location.reload());
      }, 5 * 1000);
    }
  }

  componentWillUnmount() {
    if (this.interval) clearInterval(this.interval);
  }

  reset() {
    this.setState({ error: undefined });
  }

  render() {
    const { error } = this.state;
    const { children } = this.props;

    if (error)
      return this.props.fallback != null ? this.props.fallback({ error, reset: this.reset }) : <Error error={error} />;

    return children;
  }
}

export default ErrorBoundary;
